import React, { useEffect, useMemo, useState } from "react";
import { Download, FileText, Search } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";

export default function Research() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/research").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const t = useMemo(() => ({
    eyebrow: isAr ? "الأبحاث القانونية" : "Legal Research",
    title: isAr
      ? "أبحاث ومذكرات من فريقنا."
      : "Research and memos from our team.",
    lead: isAr
      ? "أوراق بحثية ومذكرات قانونية أعدّها محامو مجموعة الدخيل، تتناول قضايا معاصرة في القانون الكويتي والممارسة المهنية."
      : "Research papers and legal memos prepared by Aldakheel lawyers, addressing contemporary issues in Kuwaiti law and practice.",
    search: isAr ? "ابحث بالعنوان أو المؤلف..." : "Search by title or author…",
    empty: isAr ? "لم تُنشر أبحاث بعد." : "No research has been published yet.",
    download: isAr ? "تحميل" : "Download",
    by: isAr ? "بقلم" : "By",
  }), [isAr]);

  const filtered = items.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.title || "").toLowerCase().includes(q) ||
      (r.author || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="research-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      <div className="mb-8 max-w-md relative">
        <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          dir="auto"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.search}
          data-testid="research-search"
          className="rounded-sm h-11 ps-9"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500">
          {t.empty}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {filtered.map((r) => (
            <article
              key={r.id}
              data-testid={`research-item-${r.id}`}
              className="bg-white p-8 lg:p-10 flex flex-col"
            >
              <div className="flex items-start gap-3 mb-4">
                <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                <p className="eyebrow">
                  {r.language === "ar" ? "AR" : "EN"} · {new Date(r.uploaded_at).toLocaleDateString(isAr ? "ar-KW" : "en-GB")}
                </p>
              </div>
              <h2 className="font-display text-2xl lg:text-3xl leading-tight mb-3">
                {r.title}
              </h2>
              {r.author && (
                <p className="text-sm text-slate-500 mb-4">
                  {t.by}: {r.author}
                </p>
              )}
              {r.summary && (
                <p className="text-slate-700 leading-relaxed mb-6 flex-1">
                  {r.summary}
                </p>
              )}
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/research/${r.id}/download`}
                target="_blank"
                rel="noreferrer"
                data-testid={`research-download-${r.id}`}
                className="inline-flex items-center gap-2 self-start px-4 h-10 text-xs uppercase tracking-[0.18em] border border-slate-300 hover:border-slate-900 transition-colors mt-auto"
              >
                <Download className="w-3.5 h-3.5" /> {t.download}
              </a>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
